import { useState } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';

const MIN_PASSWORD = 10;

export default function Login() {
  const { signIn, signUp } = useAuth();
  const [mode, setMode] = useState<'signin' | 'signup' | 'forgot'>('signin');
  const [sent, setSent] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const creating = mode === 'signup';
  const forgot = mode === 'forgot';

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    // Checked here as well as on the server so the failure is immediate.
    if (creating && password.length < MIN_PASSWORD) {
      setError(`Password must be at least ${MIN_PASSWORD} characters`);
      return;
    }

    setBusy(true);
    try {
      if (forgot) {
        await api.forgotPassword(email);
        setSent(true);
        return;
      }
      await (creating ? signUp(email, password) : signIn(email, password));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="auth-screen">
      <form className="auth-card" onSubmit={submit}>
        <div className="logo auth-logo">
          <span className="logo-mark" aria-hidden>Δ</span>
          DeltaPicks
        </div>
        <p className="auth-sub">
          {forgot
            ? 'Enter your email and we will send you a link to choose a new password.'
            : creating ? 'Create an account to start tracking.' : 'Sign in to your workstation.'}
        </p>

        <label className="field">
          <span className="label-text">Email</span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="username"
            required
            autoFocus
          />
        </label>

        {!forgot && (
        <label className="field">
          <span className="label-text">Password</span>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete={creating ? 'new-password' : 'current-password'}
            required
          />
          {creating && (
            <span className="label-hint">At least {MIN_PASSWORD} characters.</span>
          )}
        </label>
        )}

        {forgot && sent && (
          <div className="auth-ok">
            If {email} has an account, a reset link is on its way. It expires in 1 hour.
          </div>
        )}

        {error && <div className="auth-error">{error}</div>}

        <button className="btn btn-primary" type="submit" disabled={busy || (forgot && sent)} style={{ width: '100%' }}>
          {busy ? 'Working…' : forgot ? (sent ? 'Link sent' : 'Send reset link') : creating ? 'Create account' : 'Sign in'}
        </button>

        {mode === 'signin' && (
          <button
            type="button"
            className="btn btn-ghost btn-sm auth-switch"
            onClick={() => {
              setMode('forgot');
              setSent(false);
              setError(null);
            }}
          >
            Forgot password?
          </button>
        )}

        <button
          type="button"
          className="btn btn-ghost btn-sm auth-switch"
          onClick={() => {
            setMode(mode === 'signin' ? 'signup' : 'signin');
            setError(null);
          }}
        >
          {mode === 'signin' ? 'Need an account? Create one' : forgot ? 'Back to sign in' : 'Already have an account? Sign in'}
        </button>

        <p className="auth-foot">
          Your ledger and model settings are private to your account. DeltaPicks is a research
          tool, not betting advice. <a href="/privacy" style={{ textDecoration: 'underline' }}>Privacy</a>
        </p>
      </form>
    </div>
  );
}
